import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  siteName,
  defaultTitle,
  defaultDescription,
  getRouteDefaults,
} from "../config/seoConfig";
import { languageConfig } from "../config/languageConfig";

/**
 * Hook to set document title, meta description, meta keywords, canonical URL,
 * and optional hreflang alternate links for multilingual SEO.
 *
 * @param {Object} options
 * @param {string} [options.title] - Page title (without site name; " | Kliq CRM" is appended)
 * @param {string} [options.description] - Meta description
 * @param {string} [options.keywords] - Meta keywords
 * @param {string} [options.canonicalUrl] - Canonical URL (defaults to current origin + pathname)
 * @param {boolean} [options.hreflang] - If true, inject hreflang alternate links for all supported languages
 */
export function useSEO(options = {}) {
  const { pathname } = useLocation();
  const routeDefaults = getRouteDefaults(pathname);

  const title = options.title ?? routeDefaults?.title ?? null;
  const description =
    options.description ?? routeDefaults?.description ?? defaultDescription;
  const keywords = options.keywords ?? routeDefaults?.keywords ?? null;
  const canonicalUrl =
    options.canonicalUrl ??
    (typeof window !== "undefined"
      ? `${window.location.origin}${pathname}`
      : null);
  const enableHreflang = options.hreflang === true;

  useEffect(() => {
    document.title = title ? `${title} | ${siteName}` : defaultTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", description);

    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement("meta");
        metaKeywords.setAttribute("name", "keywords");
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute("content", keywords);
    } else {
      const existing = document.querySelector('meta[name="keywords"]');
      if (existing) existing.remove();
    }

    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!linkCanonical) {
        linkCanonical = document.createElement("link");
        linkCanonical.setAttribute("rel", "canonical");
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute("href", canonicalUrl);
    } else if (linkCanonical) {
      linkCanonical.remove();
    }

    const removeHreflang = () => {
      document
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove());
    };

    if (enableHreflang && typeof window !== "undefined") {
      removeHreflang();
      const origin = window.location.origin;
      // Path without language prefix (e.g. /es/features -> /features)
      const pathWithoutLang = pathname.replace(
        /^\/[a-z]{2}(-[a-z0-9]+)?(\/|$)/gi,
        (_, __, rest) => rest || "/"
      );
      const basePath = pathWithoutLang || "/";

      languageConfig.supportedLanguages.forEach((lang) => {
        const href =
          lang.default || lang.urlCode === languageConfig.defaultLanguage
            ? `${origin}${basePath}`
            : `${origin}/${lang.urlCode}${basePath}`;
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang.code);
        link.setAttribute("href", href);
        document.head.appendChild(link);
      });

      const xDefault = languageConfig.supportedLanguages.find(
        (l) => l.default || l.code === languageConfig.defaultLanguage
      );
      if (xDefault) {
        const href = `${origin}${basePath}`;
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", "x-default");
        link.setAttribute("href", href);
        document.head.appendChild(link);
      }
    } else {
      removeHreflang();
    }

    return () => {
      document.title = defaultTitle;
      removeHreflang();
    };
  }, [
    pathname,
    title,
    description,
    keywords,
    canonicalUrl,
    enableHreflang,
  ]);

  return null;
}

export default useSEO;
